import { Injectable } from '@angular/core';
import { Actions, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { ButtonsTypes } from 'estensa-module';
import { each, values } from 'lodash-es';
import { BehaviorSubject, take } from 'rxjs';

import { EWhere } from '../../services/tp-integrations/tp-integrations.interface';
import { CustomPageType } from '../../custom-pages/cp-enum';
import { seSetToolbarInfo } from '../../root-store/root.actions';
import { selectSeSearchSliders } from '../../root-store/root.selectors';
import { RootState } from '../../root-store/root.state';
import { SearchPageConfigurationService } from '../../search-page/search-page-configuration.service';
import { EstensaFormType, SearchFormType } from '../../search-page/search-page-type.enum';
import { ButtonsService } from '../../services/angularjs/estensa-buttons.service';
import { TpIntegrationsService } from '../../services/tp-integrations/tp-integrations.service';
import { seToolbarInitialize } from './redux/se-buttons-toolbar.actions';
import { SLIDER_OPTIONS, TIMELINE_OPTIONS } from './redux/se-buttons-toolbar.state';
import { ExtraButton, mkButtonData, SlidersData, ToolbarAction, ToolbarButtonData } from './types';
import { ICustomPageToolbar } from '../../custom-pages/resolvers/toolbar-data';
import { RadioSettingsService } from '../../services/settings/radio-settings.service';
import { TranslateService } from '@ngx-translate/core';

type SlidersKey = CustomPageType | SearchFormType | EstensaFormType;

@Injectable({
    providedIn: 'root',
})
export class SeButtonsToolbarService {
    leftSliderStatus$ = this.store.select('buttonsToolbar', 'leftSlider');

    rightSliderStatus$ = this.store.select('buttonsToolbar', 'rightSlider');

    timelineStatus$ = this.store.select('buttonsToolbar', 'timeline');

    get currentButtonsData$() {
        return this.currentButtonsDataSubject.asObservable();
    }

    private slidersInitialData: Partial<Record<SlidersKey, SlidersData>> = {};

    private buttonsData: Partial<Record<SlidersKey, ToolbarButtonData[]>> = {};

    private currentButtonsDataSubject = new BehaviorSubject<ToolbarButtonData[]>([]);

    constructor(
        private actions: Actions,
        private buttonsService: ButtonsService,
        private store: Store<RootState>,
        private searchPageConfigurationService: SearchPageConfigurationService,
        private tpIntegrations: TpIntegrationsService,
        private settingsService: RadioSettingsService,
        private translate: TranslateService,
    ) {
        SeButtonsToolbarService.checkValueConsistency();

        this.initSearchPagesButtonsData();
        this.initCustomPagesButtonsData();
        this.initEstensaPagesButtonsData();
        this.initThirdPartButtonsData();

        this.initSliderData();
        this.initAuthorizations();

        this.actions.pipe(ofType(seToolbarInitialize)).subscribe((action) => {
            this.currentButtonsDataSubject.next(action.buttons);
        });
    }

    private initThirdPartButtonsData() {
        /*creare action che aggiungano questi buttons direttamente nello store*/

        this.buttonsData[CustomPageType.CARDIOLOGY_VISIT] = this.tpIntegrations.mergeButtonsData(
            this.buttonsData[CustomPageType.CARDIOLOGY_VISIT] || [],
            EWhere.CUSTOM_PAGE_CARDIOLOGY_VISIT,
        );

        this.buttonsData[CustomPageType.TELECONSULTATION] = this.tpIntegrations.mergeButtonsData(
            this.buttonsData[CustomPageType.TELECONSULTATION] || [],
            EWhere.CUSTOM_PAGE_TELECONSULTATION,
        );

        this.buttonsData[CustomPageType.TELEMONITORING_VISIT] = this.tpIntegrations.mergeButtonsData(
            this.buttonsData[CustomPageType.TELEMONITORING_VISIT] || [],
            EWhere.CUSTOM_PAGE_TELEMONITORING_VISIT,
        );

        this.buttonsData[CustomPageType.ULTRASOUND] = this.tpIntegrations.mergeButtonsData(
            this.buttonsData[CustomPageType.ULTRASOUND] || [],
            EWhere.CUSTOM_PAGE_ULTRASOUND,
        );

        this.buttonsData[CustomPageType.SPECIALTY] = this.tpIntegrations.mergeButtonsData(
            this.buttonsData[CustomPageType.SPECIALTY] || [],
            EWhere.CUSTOM_PAGE_SPECIALTY,
        );

        this.buttonsData[SearchFormType.VISIT] = this.tpIntegrations.mergeButtonsData(
            this.buttonsData[SearchFormType.VISIT] || [],
            EWhere.LIST_PAGE_VISIT,
        );

        this.buttonsData[SearchFormType.ECG] = this.tpIntegrations.mergeButtonsData(
            this.buttonsData[SearchFormType.ECG] || [],
            EWhere.LIST_PAGE_ECG,
        );

        this.buttonsData[EstensaFormType.ECG_REPORTING] = this.tpIntegrations.mergeButtonsData(
            this.buttonsData[EstensaFormType.ECG_REPORTING] || [],
            EWhere.ECG_PAGE,
        );

        this.buttonsData[SearchFormType.COMPLETE_CD] = this.tpIntegrations.mergeButtonsData(
            this.buttonsData[SearchFormType.COMPLETE_CD] || [],
            EWhere.LIST_PAGE_COMPLETE_CD,
        );

        this.buttonsData[SearchFormType.COLLABORATION] = this.tpIntegrations.mergeButtonsData(
            this.buttonsData[SearchFormType.COLLABORATION] || [],
            EWhere.LIST_PAGE_COLLABORATION,
        );

        this.buttonsData[SearchFormType.ON_PRINTING_CD] = this.tpIntegrations.mergeButtonsData(
            this.buttonsData[SearchFormType.ON_PRINTING_CD] || [],
            EWhere.LIST_PAGE_ON_PRINTING_CD,
        );

        this.buttonsData[SearchFormType.TELEMONITORING] = this.tpIntegrations.mergeButtonsData(
            this.buttonsData[SearchFormType.TELEMONITORING] || [],
            EWhere.LIST_PAGE_TELEMONITORING,
        );

        this.buttonsData[SearchFormType.NRE] = this.tpIntegrations.mergeButtonsData(
            this.buttonsData[SearchFormType.NRE] || [],
            EWhere.LIST_PAGE_NRE,
        );

        this.buttonsData[SearchFormType.SPECIALTY] = this.tpIntegrations.mergeButtonsData(
            this.buttonsData[SearchFormType.SPECIALTY] || [],
            EWhere.LIST_PAGE_SPECIALTY,
        );

        this.buttonsData[SearchFormType.EXECUTION] = this.tpIntegrations.mergeButtonsData(
            this.buttonsData[SearchFormType.EXECUTION] || [],
            EWhere.EXECUTION,
        );
    }

    private initAuthorizations() {
        each(this.buttonsData, (buttons, key, buttonsData) => {
            if (buttonsData[key as SlidersKey])
                buttonsData[key as SlidersKey] =
                    buttons?.filter((button) => this.buttonsService.isButtonTypeAuthorized(button.permission)) ?? [];
        });
    }

    /// Assegna i valori di default ovvero di partenza degli slider per le singole pagine
    private initSliderData() {
        // Pagine Generiche
        this.slidersInitialData[EstensaFormType.ECG_REPORTING] = {
            initialized: false,
            leftSlider: SLIDER_OPTIONS.LEFT_SLIDER_DISABLED,
            rightSlider: SLIDER_OPTIONS.RIGHT_SLIDER_OVERLAP_CLOSED,
            timeline: TIMELINE_OPTIONS.NO_TIMELINE,
        };
        this.slidersInitialData[EstensaFormType.TELECONSULTATION] = {
            initialized: false,
            leftSlider: SLIDER_OPTIONS.LEFT_SLIDER_DISABLED,
            rightSlider: SLIDER_OPTIONS.RIGHT_SLIDER_DISABLED,
            timeline: TIMELINE_OPTIONS.NO_TIMELINE,
        };
        /* Default Text Management */
        this.slidersInitialData[EstensaFormType.DEFAULTTEXTMGNT] = {
            initialized: false,
            leftSlider: SLIDER_OPTIONS.LEFT_SLIDER_DISABLED,
            rightSlider: SLIDER_OPTIONS.RIGHT_SLIDER_DISABLED,
            timeline: TIMELINE_OPTIONS.NO_TIMELINE,
        };

        // Search Pages

        /* ECG  */
        this.slidersInitialData[SearchFormType.ECG] = {
            initialized: false,
            leftSlider: SLIDER_OPTIONS.LEFT_SLIDER_OPEN,
            rightSlider: SLIDER_OPTIONS.RIGHT_SLIDER_CLOSED,
            timeline: TIMELINE_OPTIONS.TIMELINE_CLOSED, // TODO_DGS implementare quando saranno disponibili eventuali settings
        };

        // Applico il settings sullo stato iniziale ECG del pannello
        if (
            this.searchPageConfigurationService.getIsRightPanelVisibleOnStart(SearchFormType.ECG) &&
            !!this.slidersInitialData[SearchFormType.ECG]
        )
            this.slidersInitialData[SearchFormType.ECG].rightSlider = SLIDER_OPTIONS.RIGHT_SLIDER_OPEN;

        /* VISIT  */
        this.slidersInitialData[SearchFormType.VISIT] = {
            initialized: false,
            leftSlider: SLIDER_OPTIONS.LEFT_SLIDER_OPEN,
            rightSlider: SLIDER_OPTIONS.RIGHT_SLIDER_CLOSED,
            timeline: TIMELINE_OPTIONS.TIMELINE_CLOSED, // TODO_DGS implementare quando saranno disponibili eventuali settings
        };

        // Applico il settings sullo stato iniziale VISIT del pannello
        if (
            this.searchPageConfigurationService.getIsRightPanelVisibleOnStart(SearchFormType.VISIT) &&
            this.slidersInitialData[SearchFormType.VISIT]
        )
            this.slidersInitialData[SearchFormType.VISIT].rightSlider = SLIDER_OPTIONS.RIGHT_SLIDER_OPEN;

        /* COMPLETE_CD  */
        this.slidersInitialData[SearchFormType.COMPLETE_CD] = {
            initialized: false,
            leftSlider: SLIDER_OPTIONS.LEFT_SLIDER_OPEN,
            rightSlider: SLIDER_OPTIONS.RIGHT_SLIDER_DISABLED,
            timeline: TIMELINE_OPTIONS.TIMELINE_DISABLED,
        };

        /* COLLABORATION  */
        this.slidersInitialData[SearchFormType.COLLABORATION] = {
            initialized: false,
            leftSlider: SLIDER_OPTIONS.LEFT_SLIDER_DISABLED,
            rightSlider: SLIDER_OPTIONS.RIGHT_SLIDER_DISABLED,
            timeline: TIMELINE_OPTIONS.TIMELINE_DISABLED,
        };

        /* ON_PRINTING_CD  */
        this.slidersInitialData[SearchFormType.ON_PRINTING_CD] = {
            initialized: false,
            leftSlider: SLIDER_OPTIONS.LEFT_SLIDER_OPEN,
            rightSlider: SLIDER_OPTIONS.RIGHT_SLIDER_DISABLED,
            timeline: TIMELINE_OPTIONS.TIMELINE_DISABLED,
        };

        /* TELEMONITORING  */
        this.slidersInitialData[SearchFormType.TELEMONITORING] = {
            initialized: false,
            leftSlider: SLIDER_OPTIONS.LEFT_SLIDER_OPEN,
            rightSlider: SLIDER_OPTIONS.RIGHT_SLIDER_OPEN,
            timeline: TIMELINE_OPTIONS.TIMELINE_DISABLED,
        };

        /* NRE  */
        this.slidersInitialData[SearchFormType.NRE] = {
            initialized: false,
            leftSlider: SLIDER_OPTIONS.LEFT_SLIDER_OPEN,
            rightSlider: SLIDER_OPTIONS.RIGHT_SLIDER_DISABLED,
            timeline: TIMELINE_OPTIONS.TIMELINE_DISABLED,
        };

        //TOOLUPLOAD
        this.slidersInitialData[SearchFormType.TOOLUPLOAD] = {
            initialized: false,
            leftSlider: SLIDER_OPTIONS.LEFT_SLIDER_DISABLED,
            rightSlider: SLIDER_OPTIONS.RIGHT_SLIDER_DISABLED,
            timeline: TIMELINE_OPTIONS.TIMELINE_DISABLED,
        };

        /* SPECIALTY */
        this.slidersInitialData[SearchFormType.SPECIALTY] = {
            initialized: false,
            leftSlider: SLIDER_OPTIONS.LEFT_SLIDER_OPEN,
            rightSlider: SLIDER_OPTIONS.RIGHT_SLIDER_CLOSED,
            timeline: TIMELINE_OPTIONS.TIMELINE_CLOSED, // TODO_DGS implementare quando saranno disponibili eventuali settings
        };

        /* DUMMY_VNEXT2 - DEBUG  */
        this.slidersInitialData[SearchFormType.DUMMY_VNEXT2] = {
            initialized: false,
            leftSlider: SLIDER_OPTIONS.LEFT_SLIDER_DISABLED,
            rightSlider: SLIDER_OPTIONS.RIGHT_SLIDER_CLOSED,
            timeline: TIMELINE_OPTIONS.TIMELINE_DISABLED,
        };

        /* EXECUTION  */
        this.slidersInitialData[SearchFormType.EXECUTION] = {
            initialized: false,
            leftSlider: SLIDER_OPTIONS.LEFT_SLIDER_DISABLED,
            rightSlider: SLIDER_OPTIONS.RIGHT_SLIDER_CLOSED,
            timeline: TIMELINE_OPTIONS.TIMELINE_DISABLED,
        };

        // Applico il settings sullo stato iniziale EXECUTION del pannello
        if (
            this.searchPageConfigurationService.getIsRightPanelVisibleOnStart(SearchFormType.EXECUTION) &&
            this.slidersInitialData[SearchFormType.EXECUTION]
        )
            this.slidersInitialData[SearchFormType.EXECUTION].rightSlider = SLIDER_OPTIONS.RIGHT_SLIDER_OPEN;

        /* CUSTOM PAGES  */
        this.slidersInitialData[CustomPageType.CARDIOLOGY_VISIT] = {
            initialized: false,
            leftSlider: SLIDER_OPTIONS.LEFT_SLIDER_OPEN,
            rightSlider: SLIDER_OPTIONS.RIGHT_SLIDER_OPEN,
            timeline: TIMELINE_OPTIONS.TIMELINE_CLOSED,
        };

        this.slidersInitialData[CustomPageType.TELECONSULTATION] = {
            initialized: false,
            leftSlider: SLIDER_OPTIONS.LEFT_SLIDER_OPEN,
            rightSlider: SLIDER_OPTIONS.RIGHT_SLIDER_OPEN,
            timeline: TIMELINE_OPTIONS.TIMELINE_CLOSED,
        };

        this.slidersInitialData[CustomPageType.TELEMONITORING_VISIT] = {
            initialized: false,
            leftSlider: SLIDER_OPTIONS.LEFT_SLIDER_OPEN,
            rightSlider: SLIDER_OPTIONS.RIGHT_SLIDER_OPEN,
            timeline: TIMELINE_OPTIONS.TIMELINE_CLOSED,
        };

        this.slidersInitialData[CustomPageType.ULTRASOUND] = {
            initialized: false,
            leftSlider: SLIDER_OPTIONS.LEFT_SLIDER_OPEN,
            rightSlider: SLIDER_OPTIONS.RIGHT_SLIDER_OPEN,
            timeline: TIMELINE_OPTIONS.TIMELINE_CLOSED,
        };

        this.slidersInitialData[CustomPageType.SPECIALTY] = {
            initialized: false,
            leftSlider: SLIDER_OPTIONS.LEFT_SLIDER_CLOSED,
            rightSlider: SLIDER_OPTIONS.RIGHT_SLIDER_OPEN,
            timeline: TIMELINE_OPTIONS.TIMELINE_CLOSED,
        };
    }

    // Inizializza la prima volta i pulsanti e lo stato delle slider per le search page
    initSearchPageToolbar(pageType: SearchFormType) {
        if (!!this.slidersInitialData[pageType] && !this.slidersInitialData[pageType]!.initialized) {
            this.slidersInitialData[pageType]!.initialized = true;

            this.store.dispatch(
                seToolbarInitialize({
                    buttons: this.getButtonsDataList(pageType),
                    leftSlider: this.slidersInitialData[pageType]!.leftSlider,
                    rightSlider: this.slidersInitialData[pageType]!.rightSlider,
                    timeline: this.slidersInitialData[pageType]!.timeline ?? TIMELINE_OPTIONS.TIMELINE_DISABLED,
                }),
            );
        } else {
            // Dal secondo giro in poi prendo lo stato salvato della pagina
            this.store
                .select(selectSeSearchSliders(pageType))
                .pipe(take(1))
                .subscribe((state) => {
                    this.store.dispatch(
                        seToolbarInitialize({
                            buttons: this.getButtonsDataList(pageType),
                            leftSlider: state.left,
                            rightSlider: state.right,
                            timeline: state.timeline,
                        }),
                    );
                });
        }
    }

    // Inizializza la prima volta i pulsanti e lo stato delle slider per le custom page
    initCustomPageToolbar(cpToolbar: ICustomPageToolbar) {
        const sliders = this.slidersInitialData[cpToolbar.config.type as SlidersKey];
        if (!sliders) {
            return;
        }

        if (!sliders.initialized) {
            this.store.dispatch(
                seSetToolbarInfo({
                    info1: cpToolbar.info1,
                    info2: cpToolbar.info2,
                }),
            );

            this.store.dispatch(
                seToolbarInitialize({
                    buttons: this.getButtonsDataList(cpToolbar.config.type),
                    leftSlider: cpToolbar.config.leftSliderConfig,
                    rightSlider: cpToolbar.config.rightSliderConfig,
                    timeline: cpToolbar.config.timelineConfig,
                }),
            );
        }
    }

    // Inizializza la prima volta i pulsanti e lo stato delle slider per pagine interne
    initEstensaPageToolbar(estensaPageType: EstensaFormType) {
        if (!!this.slidersInitialData[estensaPageType] && !this.slidersInitialData[estensaPageType]?.initialized) {
            this.store.dispatch(
                seToolbarInitialize({
                    buttons: this.getButtonsDataList(estensaPageType),
                    leftSlider: this.slidersInitialData[estensaPageType]!.leftSlider,
                    rightSlider: this.slidersInitialData[estensaPageType]!.rightSlider,
                    timeline: this.slidersInitialData[estensaPageType]!.timeline,
                }),
            );
        }
    }

    // #region Private Methods

    private initCustomPagesButtonsData() {
        // In base ai setting ReportValidation e CardioReportValidation genero gli stati di visibilita' dei pulsanti di sign e approve
        const approveCardioButtonStatus = { visible: this.settingsService.canApproveCardio(), disabled: true };
        const signCardioButtonStatus = { visible: this.settingsService.canSignCardio(), disabled: true };
        const approveRadioButtonStatus = { visible: this.settingsService.canApprove(), disabled: true };
        const signRadioButtonStatus = { visible: this.settingsService.canSign(), disabled: true };
        const undoApproveButtonStatus = { visible: this.settingsService.canUndoApprove(), disabled: true };

        // Pagina refertazione - Visite
        this.buttonsData[CustomPageType.CARDIOLOGY_VISIT] = [
            mkButtonData(ToolbarAction.SAVE, ButtonsTypes.saveReport),
            mkButtonData(ToolbarAction.DEFINITIVE, ButtonsTypes.approveReport, approveCardioButtonStatus),
            mkButtonData(
                ToolbarAction.DEFINITIVE_AND_PRINT,
                ButtonsTypes.approveReportAndPrint,
                approveCardioButtonStatus,
            ),
            mkButtonData(ToolbarAction.SIGN, ButtonsTypes.signReport, signCardioButtonStatus),
            mkButtonData(ToolbarAction.SIGN_AND_PRINT, ButtonsTypes.signReportAndPrint, signCardioButtonStatus),
            mkButtonData(ToolbarAction.PRINT_PREVIEW, ButtonsTypes.printReport),
            mkButtonData(ToolbarAction.PRINT_CERTIFICATE, ButtonsTypes.printCertificate),
            mkButtonData(ToolbarAction.UNDO_VALIDATION, ButtonsTypes.undoApproveReport, undoApproveButtonStatus),
            mkButtonData(ToolbarAction.PATIENT, ButtonsTypes.patient),
            mkButtonData(ToolbarAction.IMPORT_VISIT, ButtonsTypes.importVisit),
            mkButtonData(ToolbarAction.APPLY_NORMAL_VALUES, ButtonsTypes.applyNormalValues),
            mkButtonData(ToolbarAction.IMAGES, ButtonsTypes.images),
            mkButtonData(ToolbarAction.BURN, ButtonsTypes.burn),
            mkButtonData(ToolbarAction.ATTACHED_DOCS, ButtonsTypes.attachedDocs),
            mkButtonData(ToolbarAction.OBSCURED_DATA, ButtonsTypes.obscuredData),
            mkButtonData(ToolbarAction.VISIT_TYPE, ButtonsTypes.visitType),
            mkButtonData(ToolbarAction.FORCE_VISIT_TYPE, ButtonsTypes.forceVisitType),
            mkButtonData(ToolbarAction.CREATE_MEDICAL_PRESCRIPTION, ButtonsTypes.createMedicalPrescription),
            mkButtonData(ToolbarAction.IMPORT_INTO_VNA, ButtonsTypes.importVna),
            mkButtonData(ToolbarAction.EXTERNAL_LINKS, ButtonsTypes.externalLinks),
        ];

        this.buttonsData[CustomPageType.TELECONSULTATION] = [
            mkButtonData(ToolbarAction.TELECONSULTATION_SAVE, ButtonsTypes.editTeleconsultation),
            mkButtonData(ToolbarAction.TELECONSULTATION_APPROVE, ButtonsTypes.editTeleconsultation),
        ];

        this.buttonsData[CustomPageType.TELEMONITORING_VISIT] = [
            mkButtonData(ToolbarAction.SAVE, ButtonsTypes.saveReport),
            mkButtonData(ToolbarAction.DEFINITIVE, ButtonsTypes.approveReport, approveCardioButtonStatus),
            mkButtonData(ToolbarAction.SIGN, ButtonsTypes.signReport, signCardioButtonStatus),
            mkButtonData(ToolbarAction.PRINT_PREVIEW, ButtonsTypes.printReport),
            mkButtonData(ToolbarAction.SIGN_AND_PRINT, ButtonsTypes.signReportAndPrint, signCardioButtonStatus),
            mkButtonData(ToolbarAction.UNDO_VALIDATION, ButtonsTypes.undoApproveReport, undoApproveButtonStatus),
            mkButtonData(ToolbarAction.PATIENT, ButtonsTypes.patient),
            mkButtonData(ToolbarAction.IMPORT_VISIT, ButtonsTypes.importVisit),
            mkButtonData(ToolbarAction.APPLY_NORMAL_VALUES, ButtonsTypes.applyNormalValues),
            mkButtonData(ToolbarAction.ATTACHED_DOCS, ButtonsTypes.attachedDocs),
            mkButtonData(ToolbarAction.OBSCURED_DATA, ButtonsTypes.obscuredData),
        ];

        this.buttonsData[CustomPageType.ULTRASOUND] = [
            mkButtonData(ToolbarAction.SAVE, ButtonsTypes.saveReport),
            mkButtonData(ToolbarAction.DEFINITIVE, ButtonsTypes.approveReport, approveCardioButtonStatus),
            mkButtonData(
                ToolbarAction.DEFINITIVE_AND_PRINT,
                ButtonsTypes.approveReportAndPrint,
                approveCardioButtonStatus,
            ),
            mkButtonData(ToolbarAction.SIGN, ButtonsTypes.signReport, signCardioButtonStatus),
            mkButtonData(ToolbarAction.SIGN_AND_PRINT, ButtonsTypes.signReportAndPrint, signCardioButtonStatus),
            mkButtonData(ToolbarAction.PRINT_PREVIEW, ButtonsTypes.printReport),
            mkButtonData(ToolbarAction.UNDO_VALIDATION, ButtonsTypes.undoApproveReport, undoApproveButtonStatus),
            mkButtonData(ToolbarAction.PATIENT, ButtonsTypes.patient),
            mkButtonData(ToolbarAction.IMPORT_VISIT, ButtonsTypes.importVisit),
            mkButtonData(ToolbarAction.APPLY_NORMAL_VALUES, ButtonsTypes.applyNormalValues),
            mkButtonData(ToolbarAction.ATTACHED_DOCS, ButtonsTypes.attachedDocs),
            mkButtonData(ToolbarAction.OBSCURED_DATA, ButtonsTypes.obscuredData),
            mkButtonData(ToolbarAction.VISIT_TYPE, ButtonsTypes.visitType),
            mkButtonData(ToolbarAction.FORCE_VISIT_TYPE, ButtonsTypes.forceVisitType),
            mkButtonData(ToolbarAction.IMAGES, ButtonsTypes.images),
            mkButtonData(ToolbarAction.DELETE_EXAMINATION, ButtonsTypes.deleteReportExamination),
            mkButtonData(ToolbarAction.IMPORT_INTO_VNA, ButtonsTypes.importVna),
            mkButtonData(ToolbarAction.COLLABORATION_TEAMS, ButtonsTypes.openCollaborationForm),
            mkButtonData(ToolbarAction.BURN, ButtonsTypes.burn, { visible: true, disabled: true }),
            mkButtonData(ToolbarAction.EXTERNAL_LINKS, ButtonsTypes.externalLinks),
            mkButtonData(ToolbarAction.CREATE_MEDICAL_PRESCRIPTION, ButtonsTypes.createMedicalPrescription),
        ];

        this.buttonsData[CustomPageType.SPECIALTY] = [
            mkButtonData(ToolbarAction.SAVE, ButtonsTypes.saveReport),
            mkButtonData(ToolbarAction.DEFINITIVE, ButtonsTypes.approveReport, approveRadioButtonStatus),
            mkButtonData(
                ToolbarAction.DEFINITIVE_AND_PRINT,
                ButtonsTypes.approveReportAndPrint,
                approveRadioButtonStatus,
            ),
            mkButtonData(ToolbarAction.SIGN, ButtonsTypes.signReport, signRadioButtonStatus),
            mkButtonData(ToolbarAction.SIGN_AND_PRINT, ButtonsTypes.signReportAndPrint, signRadioButtonStatus),
            mkButtonData(ToolbarAction.PRINT_PREVIEW, ButtonsTypes.printReport),
            mkButtonData(ToolbarAction.UNDO_VALIDATION, ButtonsTypes.undoApproveReport, undoApproveButtonStatus),
            mkButtonData(ToolbarAction.PATIENT, ButtonsTypes.patient),
            mkButtonData(ToolbarAction.VIEW_ANAMNESTIC_DATA, ButtonsTypes.viewAnamnesticData),
            mkButtonData(ToolbarAction.TELECONSULTATION_VIEW_REQUEST, ButtonsTypes.viewConsultationRequest),
            mkButtonData(
                {
                    action: ToolbarAction.EXAM,
                    label: 'ToolbarModifyExams',
                },
                ButtonsTypes.assignExam,
                { visible: true, disabled: true },
                [
                    mkButtonData(ToolbarAction.ADD_EXAM_TO_REPORT, ButtonsTypes.assignExam),
                    mkButtonData(ToolbarAction.REMOVE_EXAM, ButtonsTypes.assignExam),
                ],
            ),
            mkButtonData(ToolbarAction.IMPORT_VISIT, ButtonsTypes.importVisit),
            mkButtonData(ToolbarAction.APPLY_NORMAL_VALUES, ButtonsTypes.applyNormalValues),
            mkButtonData(ToolbarAction.PRINT_CERTIFICATE, ButtonsTypes.printCertificate),
            mkButtonData(ToolbarAction.IMAGES, ButtonsTypes.images),
            mkButtonData(
                ToolbarAction.BURN,
                ButtonsTypes.burn,
                { visible: true, disabled: true },
                [],
                this.translate.instant('BurnOnDefaultDevice'),
            ),
            mkButtonData(
                ToolbarAction.BURN_OTHER_DEVICE,
                ButtonsTypes.burn,
                { visible: true, disabled: true },
                [],
                this.translate.instant('BurnOnOtherDevice'),
            ),
            mkButtonData(ToolbarAction.ATTACHED_DOCS, ButtonsTypes.attachedDocs),
            mkButtonData(ToolbarAction.OBSCURED_DATA, ButtonsTypes.obscuredData),
            mkButtonData(ToolbarAction.VISIT_TYPE, ButtonsTypes.visitType),
            mkButtonData(ToolbarAction.FORCE_VISIT_TYPE, ButtonsTypes.forceVisitType),
            mkButtonData(ToolbarAction.CREATE_MEDICAL_PRESCRIPTION, ButtonsTypes.createMedicalPrescription),
            mkButtonData(ToolbarAction.IMPORT_INTO_VNA, ButtonsTypes.importVna),
            mkButtonData(ToolbarAction.EXTERNAL_LINKS, ButtonsTypes.externalLinks),
        ];
    }

    private initSearchPagesButtonsData() {
        // In base ai setting ReportValidation e CardioReportValidation genero gli stati di visibilita' dei pulsanti di sign e approve
        const approveCardioButtonStatus = { visible: this.settingsService.canApproveCardio(), disabled: true };
        const signCardioButtonStatus = { visible: this.settingsService.canSignCardio(), disabled: true };
        const approveRadioButtonStatus = { visible: this.settingsService.canApprove(), disabled: true };
        const signRadioButtonStatus = { visible: this.settingsService.canSign(), disabled: true };
        const undoApproveButtonStatus = { visible: this.settingsService.canUndoApprove(), disabled: true };

        // LISTE

        //SPECIALTY
        this.buttonsData[SearchFormType.SPECIALTY] = [
            mkButtonData(ToolbarAction.OPEN, ButtonsTypes.editReport, { visible: true, disabled: true }),
            mkButtonData(ToolbarAction.ADMISSION, ButtonsTypes.directAdmission, { visible: true, disabled: true }),
            mkButtonData(
                {
                    action: ToolbarAction.EXAM,
                    label: 'ToolbarModifyExams',
                },
                ButtonsTypes.assignExam,
                { visible: true, disabled: true },
                [
                    {
                        actionType: ToolbarAction.ADD_EXAM,
                        label: ToolbarAction.ADD_EXAM,
                        isToggle: false,
                        permission: ButtonsTypes.addExam,
                        status: { visible: true, disabled: true },
                    },
                    {
                        actionType: ToolbarAction.DELETE_EXAMINATION,
                        label: ToolbarAction.DELETE_EXAMINATION,
                        isToggle: false,
                        permission: ButtonsTypes.deleteExamination,
                        status: { visible: true, disabled: true },
                    },
                    {
                        actionType: ToolbarAction.EDIT_EXAMINATION,
                        label: ToolbarAction.EDIT_EXAMINATION,
                        isToggle: false,
                        permission: ButtonsTypes.editExamination,
                        status: { visible: true, disabled: true },
                    },
                    {
                        actionType: ToolbarAction.EDIT_EXAM,
                        label: ToolbarAction.EDIT_EXAM,
                        isToggle: false,
                        permission: ButtonsTypes.editExam,
                        status: { visible: true, disabled: true },
                    },
                    {
                        actionType: ToolbarAction.ADD_EXAM_TO_REPORT,
                        label: ToolbarAction.ADD_EXAM_TO_REPORT,
                        isToggle: false,
                        permission: ButtonsTypes.assignExam,
                        status: { visible: true, disabled: true },
                    },
                    {
                        actionType: ToolbarAction.REMOVE_EXAM,
                        label: ToolbarAction.REMOVE_EXAM,
                        isToggle: false,
                        permission: ButtonsTypes.deleteExamination,
                        status: { visible: true, disabled: true },
                    },
                ],
            ),
            mkButtonData(ToolbarAction.SIGN, ButtonsTypes.editReport, { visible: true, disabled: true }),
            mkButtonData(
                ToolbarAction.RESIGN,
                ButtonsTypes.reSign,
                {
                    visible: true,
                    disabled: true,
                },
                [],
                '',
                { type: ExtraButton.BADGE, value: 0 },
            ),
            mkButtonData(ToolbarAction.DEFINITIVE, ButtonsTypes.editReport, approveRadioButtonStatus),
            mkButtonData(ToolbarAction.UNDO_VALIDATION, ButtonsTypes.editReport, undoApproveButtonStatus),
            mkButtonData(ToolbarAction.ANNULMENT, ButtonsTypes.annulmentReport, { visible: true, disabled: true }),
            mkButtonData(ToolbarAction.ANNULMENT_AND_DELETE, ButtonsTypes.annulmentReport, {
                visible: true,
                disabled: true,
            }),
            mkButtonData(ToolbarAction.PRINT_PREVIEW, ButtonsTypes.printReport, { visible: true, disabled: true }),
            mkButtonData(
                ToolbarAction.BURN,
                ButtonsTypes.printReport,
                { visible: true, disabled: true },
                [],
                this.translate.instant('BurnOnDefaultDevice'),
            ),
            mkButtonData(
                ToolbarAction.BURN_OTHER_DEVICE,
                ButtonsTypes.burnOtherDevice,
                { visible: true, disabled: true },
                [],
                this.translate.instant('BurnOnOtherDevice'),
            ),
            mkButtonData(ToolbarAction.IMAGES, ButtonsTypes.images, { visible: true, disabled: true }),
            mkButtonData(ToolbarAction.ATTACHED_DOCS, ButtonsTypes.attachedDocs, { visible: true, disabled: true }),
            mkButtonData(
                { action: ToolbarAction.PRINTS, label: ToolbarAction.PRINTS },
                ButtonsTypes.print,
                {
                    visible: true,
                    disabled: false,
                },
                [
                    {
                        actionType: ToolbarAction.PRINT_LIST,
                        label: ToolbarAction.PRINT_LIST,
                        isToggle: false,
                        status: { visible: true, disabled: false },
                        permission: ButtonsTypes.printList,
                    },
                    {
                        actionType: ToolbarAction.PRINT_MODULES,
                        label: ToolbarAction.PRINT_MODULES,
                        isToggle: false,
                        status: { visible: true, disabled: false },
                        permission: ButtonsTypes.printModules,
                    },
                    {
                        actionType: ToolbarAction.PRINT_LABELS,
                        label: ToolbarAction.PRINT_LABELS,
                        isToggle: false,
                        status: { visible: true, disabled: false },
                        permission: ButtonsTypes.printLabels,
                    },
                ],
            ),
            mkButtonData(ToolbarAction.ASSIGN_EQUIPMENT, ButtonsTypes.assignEquipment, {
                visible: true,
                disabled: true,
            }),
            mkButtonData(ToolbarAction.COLLABORATION_TEAMS, ButtonsTypes.editReport, { visible: true, disabled: true }),
            mkButtonData(ToolbarAction.TELECONSULTATION_VIEW, ButtonsTypes.viewTeleconsultation, {
                visible: true,
                disabled: true,
            }),
            mkButtonData(ToolbarAction.CREATE_TELECONSULTATION, ButtonsTypes.newTeleconsultation, {
                visible: true,
                disabled: true,
            }),
            mkButtonData(ToolbarAction.EXTERNAL_LINKS, ButtonsTypes.externalLinks),
            mkButtonData(ToolbarAction.PENDING_ACTIVITIES, ButtonsTypes.pendingActivities, {
                visible: this.settingsService.radioSettings?.worklistSettings.enableAlertForDeferredReports || false,
                disabled: false,
            }),
        ];

        // VISIT
        this.buttonsData[SearchFormType.VISIT] = [
            mkButtonData(ToolbarAction.OPEN, ButtonsTypes.editReport, { visible: true, disabled: true }),
            mkButtonData(
                {
                    action: ToolbarAction.EXAM,
                    label: 'ToolbarModifyExams',
                },
                ButtonsTypes.assignExam,
                { visible: true, disabled: true },
                [
                    {
                        actionType: ToolbarAction.ADD_EXAM,
                        label: ToolbarAction.ADD_EXAM,
                        isToggle: false,
                        permission: ButtonsTypes.addExam,
                        status: { visible: true, disabled: true },
                    },
                    {
                        actionType: ToolbarAction.DELETE_EXAMINATION,
                        label: ToolbarAction.DELETE_EXAMINATION,
                        isToggle: false,
                        permission: ButtonsTypes.deleteExamination,
                        status: { visible: true, disabled: true },
                    },
                    {
                        actionType: ToolbarAction.EDIT_EXAMINATION,
                        label: ToolbarAction.EDIT_EXAMINATION,
                        isToggle: false,
                        permission: ButtonsTypes.editExamination,
                        status: { visible: true, disabled: true },
                    },
                    {
                        actionType: ToolbarAction.EDIT_EXAM,
                        label: ToolbarAction.EDIT_EXAM,
                        isToggle: false,
                        permission: ButtonsTypes.editExam,
                        status: { visible: true, disabled: false },
                    },
                    {
                        actionType: ToolbarAction.ADD_EXAM_TO_REPORT,
                        label: ToolbarAction.ADD_EXAM_TO_REPORT,
                        isToggle: false,
                        permission: ButtonsTypes.assignExam,
                        status: { visible: true, disabled: true },
                    },
                    {
                        actionType: ToolbarAction.REMOVE_EXAM,
                        label: ToolbarAction.REMOVE_EXAM,
                        isToggle: false,
                        permission: ButtonsTypes.deleteExamination,
                        status: { visible: true, disabled: true },
                    },
                ],
            ),
            mkButtonData(
                {
                    action: ToolbarAction.MERGE_EXAMS,
                    label: 'Merging',
                },
                ButtonsTypes.mergeExams,
                { visible: true, disabled: false },
                [
                    {
                        actionType: ToolbarAction.MERGE_EXAMS,
                        label: 'ToolbarMergeExams',
                        isToggle: false,
                        permission: ButtonsTypes.mergeExams,
                        status: { visible: true, disabled: true },
                    },
                    {
                        actionType: ToolbarAction.UNDO_MERGE_EXAMS,
                        label: 'ToolbarUndoMergeExams',
                        isToggle: false,
                        permission: ButtonsTypes.undoMergeExams,
                        status: { visible: true, disabled: true },
                    },
                ],
            ),

            mkButtonData(ToolbarAction.CONNECT_PATIENT, ButtonsTypes.mergePatient, { visible: true, disabled: true }),
            mkButtonData(ToolbarAction.DISCONNECT_PATIENT, ButtonsTypes.undoMergePatient, {
                visible: true,
                disabled: true,
            }),
            mkButtonData(ToolbarAction.DEFINITIVE, ButtonsTypes.approveReport, approveCardioButtonStatus),
            mkButtonData(ToolbarAction.SIGN, ButtonsTypes.signReport, signCardioButtonStatus),
            mkButtonData(ToolbarAction.RESIGN, ButtonsTypes.reSign, signRadioButtonStatus, [], '', {
                type: ExtraButton.BADGE,
                value: 0,
            }),
            mkButtonData(ToolbarAction.PRINT_PREVIEW, ButtonsTypes.printReport, { visible: true, disabled: true }),

            mkButtonData(ToolbarAction.UNDO_VALIDATION, ButtonsTypes.undoApproveReport, undoApproveButtonStatus),
            mkButtonData(
                {
                    action: ToolbarAction.ADMISSION_VISIT_PARENT,
                    label: ToolbarAction.ADMISSION_VISIT_PARENT,
                },
                ButtonsTypes.directAdmission,
                { visible: true, disabled: false },
                [
                    {
                        actionType: ToolbarAction.NEW_EXAM,
                        label: ToolbarAction.NEW_EXAM,
                        isToggle: false,
                        permission: ButtonsTypes.admissionNewExam,
                        status: { visible: true, disabled: false },
                    },
                    {
                        actionType: ToolbarAction.BOOKED_EXAMS,
                        label: ToolbarAction.BOOKED_EXAMS,
                        isToggle: false,
                        permission: ButtonsTypes.admissionBookedExams,
                        status: { visible: true, disabled: false },
                    },
                ],
            ),
            //mkButtonData(ToolbarAction.BOOKED_EXAMS, ButtonsTypes.admissionBookedExams, { visible: true, disabled: true }),
            mkButtonData(ToolbarAction.BURN, ButtonsTypes.burn),
            mkButtonData(ToolbarAction.IMAGES, ButtonsTypes.images, { visible: true, disabled: true }),
            mkButtonData(ToolbarAction.ATTACHED_DOCS, ButtonsTypes.attachedDocs, { visible: true, disabled: true }),
            mkButtonData(ToolbarAction.ANNULMENT, ButtonsTypes.annulmentReport, { visible: true, disabled: true }),
            mkButtonData(ToolbarAction.ANNULMENT_AND_DELETE, ButtonsTypes.annulmentReport, {
                visible: true,
                disabled: true,
            }),
            mkButtonData(ToolbarAction.REPORT_WITHOUT_TRACE, ButtonsTypes.reportWithoutTrace, {
                visible: true,
                disabled: true,
            }),
            mkButtonData(ToolbarAction.ASSIGN_EQUIPMENT, ButtonsTypes.assignEquipment, {
                visible: true,
                disabled: true,
            }),
            mkButtonData(ToolbarAction.CREATE_MEDICAL_PRESCRIPTION, ButtonsTypes.createMedicalPrescription, {
                visible: false,
                disabled: true,
            }),
            mkButtonData(ToolbarAction.EXTERNAL_LINKS, ButtonsTypes.externalLinks),
            mkButtonData(ToolbarAction.COLLABORATION_TEAMS, ButtonsTypes.openCollaborationForm, {
                visible: true,
                disabled: true,
            }),
            mkButtonData(ToolbarAction.SUSPEND, ButtonsTypes.suspend, { visible: true, disabled: true }),
            mkButtonData(ToolbarAction.UNDO_SUSPENSION, ButtonsTypes.undoSuspension, { visible: true, disabled: true }),
        ];

        // ECG
        this.buttonsData[SearchFormType.ECG] = [
            mkButtonData(ToolbarAction.OPEN, ButtonsTypes.editReport, { visible: true, disabled: true }),
            mkButtonData(ToolbarAction.REPORT_WITHOUT_TRACE, ButtonsTypes.reportWithoutTrace, {
                visible: true,
                disabled: true,
            }),
            mkButtonData(ToolbarAction.MERGE_EXAMS, ButtonsTypes.mergeExams, { visible: true, disabled: true }),
            mkButtonData(ToolbarAction.UNDO_MERGE_EXAMS, ButtonsTypes.undoMergeExams, {
                visible: true,
                disabled: true,
            }),
            mkButtonData(ToolbarAction.CONNECT_PATIENT, ButtonsTypes.mergePatient, { visible: true, disabled: true }),
            mkButtonData(ToolbarAction.DISCONNECT_PATIENT, ButtonsTypes.undoMergePatient, {
                visible: true,
                disabled: true,
            }),
            mkButtonData(ToolbarAction.DEFINITIVE, ButtonsTypes.approveReport, approveCardioButtonStatus),
            mkButtonData(ToolbarAction.SIGN, ButtonsTypes.signReport, signCardioButtonStatus),
            mkButtonData(ToolbarAction.SIGN_AND_PRINT, ButtonsTypes.signReportAndPrint, signCardioButtonStatus),
            mkButtonData(ToolbarAction.RESIGN, ButtonsTypes.reSign, signCardioButtonStatus, [], '', {
                type: ExtraButton.BADGE,
                value: 0,
            }),
            mkButtonData(ToolbarAction.PRINT_PREVIEW, ButtonsTypes.printReport, { visible: true, disabled: true }),
            mkButtonData(ToolbarAction.UNDO_VALIDATION, ButtonsTypes.undoApproveReport, undoApproveButtonStatus),
            mkButtonData(ToolbarAction.ADD_EXAM, ButtonsTypes.addExam, { visible: true, disabled: true }),
            mkButtonData(ToolbarAction.ATTACHED_DOCS, ButtonsTypes.attachedDocs, { visible: true, disabled: true }),
            mkButtonData(ToolbarAction.ANNULMENT, ButtonsTypes.annulmentReport, { visible: true, disabled: true }),
            mkButtonData(ToolbarAction.ANNULMENT_AND_DELETE, ButtonsTypes.annulmentReport, {
                visible: true,
                disabled: true,
            }),
            mkButtonData(ToolbarAction.EXTERNAL_LINKS, ButtonsTypes.externalLinks),
            mkButtonData(ToolbarAction.SUSPEND, ButtonsTypes.suspend, { visible: true, disabled: true }),
            mkButtonData(ToolbarAction.UNDO_SUSPENSION, ButtonsTypes.undoSuspension, { visible: true, disabled: true }),
            mkButtonData(ToolbarAction.CREATE_MEDICAL_PRESCRIPTION, ButtonsTypes.createMedicalPrescription),
        ];

        // COMPLETE_CD
        this.buttonsData[SearchFormType.COMPLETE_CD] = [
            mkButtonData(ToolbarAction.COPY, ButtonsTypes.copyCD, { visible: true, disabled: true }),
        ];

        // COLLABORATION
        this.buttonsData[SearchFormType.COLLABORATION] = [];

        // ON_PRINTING_CD
        this.buttonsData[SearchFormType.ON_PRINTING_CD] = [
            mkButtonData(ToolbarAction.UNLOCK_MPS_CD, ButtonsTypes.unlockCD, { visible: true, disabled: true }),
            mkButtonData(ToolbarAction.DELETE, ButtonsTypes.deleteCD, { visible: true, disabled: true }),
        ];

        // TELEMONITORING
        this.buttonsData[SearchFormType.TELEMONITORING] = [
            mkButtonData(ToolbarAction.OPEN_DIARY, ButtonsTypes.openDiary, { visible: true, disabled: true }),
            mkButtonData(ToolbarAction.PATIENT_ENROLMENT, ButtonsTypes.patientEnrolment, {
                visible: true,
                disabled: false,
            }),
            mkButtonData(ToolbarAction.CREATE_REMOTE_VISIT, ButtonsTypes.createRemoteVisit, {
                visible: true,
                disabled: true,
            }),
            mkButtonData(ToolbarAction.MONITORING_KIT_ASSOCIATION, ButtonsTypes.monitoringKitAssociation, {
                visible: true,
                disabled: true,
            }),
            mkButtonData(ToolbarAction.PATIENT_CARD_AND_ALARMS, ButtonsTypes.patientCardAndAlarms, {
                visible: true,
                disabled: true,
            }),
            mkButtonData(ToolbarAction.PERSONAL_FOLDER, ButtonsTypes.personalFolder, { visible: true, disabled: true }),
            mkButtonData(ToolbarAction.COMMUNICATION, ButtonsTypes.communication, { visible: true, disabled: true }),
            mkButtonData(ToolbarAction.PATIENT_DOCUMENT, ButtonsTypes.patientDocument, {
                visible: true,
                disabled: true,
            }),
            mkButtonData(ToolbarAction.COMPILE_NOTE, ButtonsTypes.compileNote, { visible: true, disabled: true }),
            mkButtonData(ToolbarAction.ATTACHED_DOCS, ButtonsTypes.attachedDocs, { visible: true, disabled: true }),
        ];

        // NRE
        this.buttonsData[SearchFormType.NRE] = [
            mkButtonData(ToolbarAction.CREATE_MEDICAL_PRESCRIPTION, ButtonsTypes.createMedicalPrescription, {
                visible: true,
                disabled: false,
            }),
        ];

        this.buttonsData[SearchFormType.DUMMY_VNEXT2] = [
            mkButtonData(ToolbarAction.DUMMY_VNEXT2_TEST_ACTION, ButtonsTypes.dummyVnex2TestAction, {
                visible: true,
                disabled: false,
            }),
        ];
        //EXECUTION
        this.buttonsData[SearchFormType.EXECUTION] = [
            mkButtonData(ToolbarAction.ADMISSION, ButtonsTypes.directAdmission, { visible: true, disabled: true }),
            mkButtonData(
                {
                    action: ToolbarAction.PATIENT,
                    label: 'Patient',
                },
                ButtonsTypes.patient,
                { visible: true, disabled: true },
                [
                    {
                        actionType: ToolbarAction.PATIENT_REGISTRY,
                        label: 'MasterData',
                        isToggle: false,
                        permission: ButtonsTypes.patientRegistry,
                        status: { visible: true, disabled: true },
                    },
                    {
                        actionType: ToolbarAction.RADIOLOGY_FOLDER,
                        label: 'RadiologyFolder',
                        isToggle: false,
                        permission: ButtonsTypes.patientFolder,
                        status: { visible: true, disabled: true },
                    },
                ],
            ),
            mkButtonData(ToolbarAction.ADMINISTRATION_DASHBOARD, ButtonsTypes.dashboard, {
                visible: true,
                disabled: true,
            }),
            mkButtonData(ToolbarAction.FEA_AUTH_MANAGEMENT, ButtonsTypes.feaAuthorizationMngt, {
                visible: true,
                disabled: true,
            }),
            mkButtonData(
                {
                    action: ToolbarAction.EXAM_MANAGEMENT,
                    label: 'ExamManagement',
                },
                ButtonsTypes.examManagement,
                { visible: true, disabled: true },
                [
                    {
                        actionType: ToolbarAction.EDIT_EXAM, // modifica esame
                        label: 'ModifyExam',
                        isToggle: false,
                        permission: ButtonsTypes.editExam,
                        status: { visible: true, disabled: true },
                    },
                    {
                        actionType: ToolbarAction.ADD_EXAM,
                        label: 'AddExam',
                        isToggle: false,
                        permission: ButtonsTypes.addExam,
                        status: { visible: true, disabled: true },
                    },
                    {
                        actionType: ToolbarAction.EXE_CANCEL,
                        label: 'DeleteExamination',
                        isToggle: false,
                        permission: ButtonsTypes.deleteExamination,
                        status: { visible: true, disabled: true },
                    },
                    {
                        actionType: ToolbarAction.EDIT_EXAMINATION, // visualizza/modifica dettagli esame
                        label: 'ModifyExamination',
                        isToggle: false,
                        permission: ButtonsTypes.editExamination,
                        status: { visible: true, disabled: true },
                    },
                ],
            ),
            mkButtonData(ToolbarAction.SEND_TO_WORKLIST, ButtonsTypes.sendToWorkList, {
                visible: true,
                disabled: true,
            }),
            mkButtonData(ToolbarAction.EXE_EXECUTE_AS_LOGGED_USER, ButtonsTypes.exeExecuteAsLoggedUser, {
                visible: true,
                disabled: true,
            }),
            mkButtonData(ToolbarAction.EXE_EXECUTE_OTHER, ButtonsTypes.exeExecuteOther, {
                visible: true,
                disabled: true,
            }),
            mkButtonData(ToolbarAction.EXE_UNDO_EXECUTION, ButtonsTypes.exeUndoExecution, {
                visible: true,
                disabled: true,
            }),
            mkButtonData(ToolbarAction.EXE_SUSPEND, ButtonsTypes.exeSuspend, { visible: true, disabled: true }),
            mkButtonData(ToolbarAction.UNDO_SUSPENSION, ButtonsTypes.undoSuspension, { visible: true, disabled: true }),
            // 126571 PAOLO COVELLI HA CHIESTO DI TOGLIERE IL PULSANTE UNLOAD
            mkButtonData(ToolbarAction.UNLOAD, ButtonsTypes.unload, { visible: true, disabled: true }),
            mkButtonData(ToolbarAction.VIEW_IMAGES, ButtonsTypes.images, { visible: true, disabled: true }),
            mkButtonData(ToolbarAction.VIEW_ANAMNESTIC_DATA, ButtonsTypes.viewAnamnesticData, {
                visible: true,
                disabled: true,
            }),
            mkButtonData(
                {
                    action: ToolbarAction.PRINTS,
                    label: 'Prints',
                },
                ButtonsTypes.print,
                { visible: true, disabled: true },
                [
                    {
                        actionType: ToolbarAction.PRINT_LABELS,
                        label: 'PrintLabel',
                        isToggle: false,
                        permission: ButtonsTypes.printLabels,
                        status: { visible: true, disabled: true },
                    },
                    {
                        actionType: ToolbarAction.PRINT_LIST,
                        label: 'PrintList',
                        isToggle: false,
                        permission: ButtonsTypes.printList,
                        status: { visible: true, disabled: true },
                    },
                    {
                        actionType: ToolbarAction.PRINT_MODULES,
                        label: 'PrintModules',
                        isToggle: false,
                        permission: ButtonsTypes.printModules,
                        status: { visible: true, disabled: true },
                    },
                ],
            ),
            mkButtonData(ToolbarAction.ATTACHED_DOCS, ButtonsTypes.attachedDocs, { visible: true, disabled: true }),
            mkButtonData(ToolbarAction.SEND_TO_REMOTE_REPORTING, ButtonsTypes.sendToRemoteReporting, {
                visible: true,
                disabled: true,
            }),
            mkButtonData(ToolbarAction.CREATE_TELECONSULTATION, ButtonsTypes.newTeleconsultation, {
                visible: true,
                disabled: true,
            }),
        ];

        this.buttonsData[EstensaFormType.DEFAULTTEXTMGNT] = [
            mkButtonData(ToolbarAction.ADD_TEXT_PREDEFINED, ButtonsTypes.addTextPredefined, {
                visible: true,
                disabled: false,
            }),
            mkButtonData(ToolbarAction.MODIFY_TEXT_PREDEFINED, ButtonsTypes.modifyTextPredefined, {
                visible: true,
                disabled: false,
            }),
            mkButtonData(ToolbarAction.DELETE_TEXT_PREDEFINED, ButtonsTypes.deleteTextPredefined, {
                visible: true,
                disabled: false,
            }),
        ];
    }

    private initEstensaPagesButtonsData() {
        const approveCardioButtonStatus = { visible: this.settingsService.canApproveCardio(), disabled: true };
        const signCardioButtonStatus = { visible: this.settingsService.canSignCardio(), disabled: true };

        this.buttonsData[EstensaFormType.ECG_REPORTING] = [
            mkButtonData(ToolbarAction.SAVE, ButtonsTypes.editReport, { visible: true, disabled: true }),

            //mkButtonData(ToolbarAction.COLLABORATION_TEAMS, ButtonsTypes.openCollaborationForm, { active: true, disabled: true }),
            mkButtonData(ToolbarAction.DEFINITIVE, ButtonsTypes.approveReport, approveCardioButtonStatus),
            mkButtonData(
                ToolbarAction.DEFINITIVE_AND_PRINT,
                ButtonsTypes.approveReportAndPrint,
                approveCardioButtonStatus,
            ),
            mkButtonData(ToolbarAction.SIGN, ButtonsTypes.signReport, signCardioButtonStatus),
            mkButtonData(ToolbarAction.SIGN_AND_PRINT, ButtonsTypes.signReportAndPrint, signCardioButtonStatus),
            mkButtonData(ToolbarAction.PRINT, ButtonsTypes.printReport, { visible: true, disabled: true }),
            mkButtonData(ToolbarAction.RESEARCH_ARCHIVE, ButtonsTypes.openResearchArchive, {
                visible: true,
                disabled: false,
            }),
            mkButtonData(ToolbarAction.CREATE_MEDICAL_PRESCRIPTION, ButtonsTypes.createMedicalPrescription, {
                visible: true,
                disabled: false,
            }),
        ];

        this.buttonsData[EstensaFormType.TELECONSULTATION] = [];
    }

    /**
     * Internal toggle toolbar button of se buttons toolbar service
     */
    private static internalToggleToolbarButton(action: ToolbarAction, list: ToolbarButtonData[]) {
        for (let btn of list) {
            if (btn.actionType == action) {
                btn.status.checked = !btn.status.checked;

                return btn.status.checked;
            } else if (btn.children && btn.children.length > 0) {
                // Eventuale sub menu
                for (let subBtn of btn.children) {
                    if (subBtn.actionType == action) {
                        subBtn.status.checked = !subBtn.status.checked;

                        return subBtn.status.checked;
                    }
                }
            }
        }

        return undefined;
    }

    private static checkValueConsistency() {
        const allButtonTypes = values(ToolbarAction);
        const tmp = allButtonTypes.filter((el, i, a) => i === a.indexOf(el));

        if (tmp.length !== allButtonTypes.length) {
            alert(
                "Ci sono dei valori duplicati nell'enumerato ToolbarActionType correggere in se-buttons-toolbar.actions.ts",
            );
        }
    }

    // #endregion Private Methods

    // #region Public Methods

    public getButtonsDataList(pageType: SlidersKey): ToolbarButtonData[] {
        return this.buttonsData[pageType] ?? [];
    }

    public static toggleToolbarButton(
        action: ToolbarAction,
        buttonsList: ToolbarButtonData[],
        hideButtonsList?: ToolbarButtonData[],
    ) {
        let toggled = SeButtonsToolbarService.internalToggleToolbarButton(action, buttonsList);
        if (toggled == undefined)
            toggled = SeButtonsToolbarService.internalToggleToolbarButton(action, hideButtonsList ?? []);

        return toggled;
    }

    // #endregion
}
